import planAllowances from '../../helpers/planAllowances.json'
import { toDateInput } from '../kit/helpers'

// Contract form shape, tier defaults and the tenant → form derivation used by
// the tenant page and its dialogs.

// The six allowance metrics in catalogue order. `kind` says how the limit
// behaves: 'hard' is enforced when records are added, 'soft' is a commercial
// review threshold that never blocks anything, 'recorded' is tracked only.
// Mirrors ALLOWANCE_METRICS / HARD_LIMIT_METRICS / SOFT_THRESHOLD_METRICS in
// server/src/helpers/subscriptionPlans.js - keep the two in step.
export const ALLOWANCE_METRICS = [
  { key: 'employees', label: 'Employees', kind: 'hard' },
  { key: 'vehicles', label: 'Vehicles', kind: 'hard' },
  { key: 'baseLocations', label: 'Base locations', kind: 'hard' },
  { key: 'ptsJourneysPerMonth', label: 'PTS journeys / month', kind: 'soft' },
  { key: 'controlRoomHoursPerMonth', label: 'Control-room hours / month', kind: 'soft' },
  { key: 'concurrentControlRooms', label: 'Concurrent control rooms', kind: 'hard' },
]

const blankLimits = () => Object.fromEntries(ALLOWANCE_METRICS.map((metric) => [metric.key, '']))

export const defaultContract = {
  trialEnd: '',
  packageName: '',
  subscriptionTier: '',
  billingCadence: 'monthly',
  monthlyFee: '',
  annualFee: '',
  currency: 'GBP',
  includedLimits: blankLimits(),
  additionalLimits: blankLimits(),
  usageReview: {
    enabled: true,
    notes: '',
  },
  stripe: {
    customerId: '',
    subscriptionId: '',
    priceId: '',
    productId: '',
    latestInvoiceId: '',
    status: '',
  },
  quote: {
    quoteReference: '',
    quotedAt: '',
    acceptedAt: '',
    acceptedBy: '',
    notes: '',
  },
  contractStartDate: '',
  renewalDate: '',
  accountManager: '',
  notes: '',
}

export const enterprisePtsPriceOptions = [
  { lookupKey: 'enterprise_pts_1500_monthly', label: 'Enterprise+ PTS - 1,500 journeys/month' },
  { lookupKey: 'enterprise_pts_2000_monthly', label: 'Enterprise+ PTS - 2,000 journeys/month' },
  { lookupKey: 'enterprise_pts_3000_monthly', label: 'Enterprise+ PTS - 3,000 journeys/month' },
  { lookupKey: 'enterprise_pts_5000_monthly', label: 'Enterprise+ PTS - 5,000 journeys/month' },
]

export const defaultQuotedSubscriptionForm = {
  lookupKey: 'enterprise_pts_1500_monthly',
  collectionMethod: 'send_invoice',
  daysUntilDue: 14,
  quoteReference: '',
  acceptedBy: '',
  contractStartDate: '',
  renewalDate: '',
  accountManager: '',
  notes: '',
}

// Package name and standard allowances per tier, read from the catalogue
// mirror (client/src/helpers/planAllowances.json) so no plan figure is typed
// here. The allowances are for display and placeholders only - they are never
// copied into the contract form.
const tierDefaults = Object.fromEntries(
  Object.values(planAllowances.plans).map((plan) => [
    plan.tier,
    { packageName: plan.name, allowances: plan.allowances },
  ]),
)

// Fee shown for a tier whose contract has none recorded (as before).
const tierFeeDefaults = { 3: 1000 }

// The plan's standard allowance for a metric: a number, or null when the plan
// does not include it (or the tier is not a standard plan).
export const planAllowanceForTier = (tier, metric) =>
  tierDefaults[Number(tier)]?.allowances?.[metric] ?? null

// 'annual' and 'annually' are both stored on older contracts; the server now
// writes 'monthly' | 'annually'.
export const normaliseCadence = (value) => {
  const cadence = String(value ?? '').toLowerCase()
  if (cadence.startsWith('annual') || cadence.startsWith('year')) return 'annually'
  if (cadence.startsWith('month')) return 'monthly'
  return ''
}

// A stored limit as a form value: the number, or blank when nothing of its own
// is stored (zero and below mean "unset" on the server).
const limitInput = (value) => {
  const number = Number(value)
  return value !== null && value !== undefined && value !== '' && Number.isFinite(number) && number > 0
    ? number
    : ''
}

const limitInputs = (limits = {}) =>
  Object.fromEntries(ALLOWANCE_METRICS.map((metric) => [metric.key, limitInput(limits?.[metric.key])]))

// Only the keys the contract form edits are carried over. Everything else on
// the contract (agreement, grants, variations, usage-review counters, legacy
// agreed limits) is server-managed and read straight from tenant.contract.
const normaliseContract = (contract = {}) => ({
  ...defaultContract,
  packageName: contract.packageName ?? '',
  subscriptionTier: contract.subscriptionTier ?? '',
  billingCadence: normaliseCadence(contract.billingCadence),
  monthlyFee: contract.monthlyFee ?? '',
  annualFee: contract.annualFee ?? '',
  currency: contract.currency ?? '',
  includedLimits: limitInputs(contract.includedLimits),
  additionalLimits: limitInputs(contract.additionalLimits),
  usageReview: {
    enabled: contract.usageReview?.enabled !== false,
    notes: contract.usageReview?.notes ?? '',
  },
  stripe: { ...defaultContract.stripe, ...(contract.stripe ?? {}) },
  quote: {
    ...defaultContract.quote,
    ...(contract.quote ?? {}),
    quotedAt: toDateInput(contract.quote?.quotedAt),
    acceptedAt: toDateInput(contract.quote?.acceptedAt),
  },
  contractStartDate: toDateInput(contract.contractStartDate),
  renewalDate: toDateInput(contract.renewalDate),
  accountManager: contract.accountManager ?? '',
  notes: contract.notes ?? '',
})

// The contract exactly as stored, in form shape - no tier defaults filled in.
// The contract editor compares against this to decide what really changed.
export const storedContractForm = (tenant = {}) => ({
  ...normaliseContract(tenant.contract),
  trialEnd: toDateInput(tenant.trialEnd),
})

const valueOrFallback = (value, fallback) =>
  value === undefined || value === null || value === '' ? fallback : value

// Form-shaped contract for the tenant page and the contract editor. Package,
// tier, cadence, fee and currency fall back to the tier's defaults as before;
// included and additional limits are exactly what is stored (blank = the plan
// default applies), so saving the form can never persist a plan default.
export const deriveContractFromTenant = (tenant = {}) => {
  const tier = Number(tenant.subscriptionTier || tenant.contract?.subscriptionTier)
  const defaults = tierDefaults[tier] || {}
  const contract = normaliseContract(tenant.contract)

  return {
    ...contract,
    trialEnd: toDateInput(tenant.trialEnd),
    packageName: valueOrFallback(contract.packageName, defaults.packageName ?? ''),
    subscriptionTier: valueOrFallback(contract.subscriptionTier, tier || ''),
    billingCadence: valueOrFallback(contract.billingCadence, 'monthly'),
    monthlyFee: valueOrFallback(contract.monthlyFee, tierFeeDefaults[tier] ?? ''),
    currency: valueOrFallback(contract.currency, 'GBP'),
    stripe: {
      ...contract.stripe,
      customerId: valueOrFallback(contract.stripe.customerId, tenant.stripeCustomerRef ?? ''),
      subscriptionId: valueOrFallback(contract.stripe.subscriptionId, tenant.stripeSubscriptionId ?? ''),
      status: valueOrFallback(contract.stripe.status, tenant.stripeSubscriptionStatus ?? ''),
    },
    renewalDate: valueOrFallback(contract.renewalDate, toDateInput(tenant.stripeSubscriptionExpiry)),
  }
}

// Whether a limit typed into the contract form can be stored: blank (no limit
// of its own - the plan default applies) or a plain number, zero or above,
// whole except for control-room hours. Mirrors the contract PUT, which refuses
// anything else rather than dropping the allowance. Returns '' when it is fine.
export const limitProblem = (metricKey, value) => {
  const text = String(value ?? '').trim()
  if (text === '') return ''
  if (!/^\d+(\.\d+)?$/.test(text)) return 'Enter a plain number, zero or above (no commas).'
  if (metricKey !== 'controlRoomHoursPerMonth' && !/^\d+$/.test(text)) return 'Enter a whole number.'
  return ''
}

const readPath = (object, path) => path.split('.').reduce((current, key) => current?.[key], object)

// The body for PUT /admincrm/tenants/:id/contract: ONLY the fields edited in
// this dialog session (`dirtyPaths`, e.g. 'monthlyFee' or
// 'includedLimits.employees') whose value now differs from what is stored
// (`stored`, from storedContractForm). The server leaves absent keys alone, so
// a value loaded earlier is never echoed back over a change someone else - or
// an Enterprise+ transition - made in the meantime, and a default the form
// merely displays is never written. A field the user cleared is sent blank,
// which unsets it.
export const buildContractPayload = (form, dirtyPaths = [], stored = {}) => {
  const body = {}
  for (const path of dirtyPaths) {
    const value = readPath(form, path) ?? ''
    const before = readPath(stored, path) ?? ''
    if (typeof value === 'boolean' ? value === before : String(value).trim() === String(before).trim()) continue
    const keys = path.split('.')
    let target = body
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        target[key] = value
      } else {
        target[key] = target[key] ?? {}
        target = target[key]
      }
    })
  }
  return body
}

// Legacy contract.agreedLimits values still stored on the tenant, as
// [{ key, label, value }]. The form no longer writes them; they stay a historic
// override until included limits are set.
export const legacyAgreedLimits = (tenant = {}) =>
  ALLOWANCE_METRICS.map((metric) => ({
    ...metric,
    value: Number(tenant.contract?.agreedLimits?.[metric.key]),
  })).filter((metric) => Number.isFinite(metric.value) && metric.value > 0)

// Mirror of hasActiveSubscription in server/_salesLeadHelpers.js for tenant
// documents fetched without the computed flag.
export const hasActiveSubscription = (tenant) => {
  if (!tenant) return false
  if (tenant.isActive === false) return false
  if (tenant.inInitialTrialPeriod !== false) return false
  if (Number(tenant.subscriptionTier ?? 0) < 1) return false
  // Manually-billed tenants are live customers regardless of leftover Stripe state.
  if (tenant.manualBilling === true) return true
  const status = String(tenant.stripeSubscriptionStatus ?? '').toLowerCase()
  return !['canceled', 'incomplete_expired'].includes(status)
}
